// src/prototype/enterprise/reports/ReportsSurface.tsx
// ============================================================
// ENTERPRISE · REPORTS SURFACE — a LIBRARY of governance-ready outputs. A row of report cards
// (from listReports) selects the report rendered below: the Exec One-Pager (design "1k") + the
// Board Rationale 4-slide deck (design "1l"), built by getReport from the saved scenario/run
// (not live lever state). Export buttons apply to the selected report (inert this phase —
// flagged). Full-width. Read-only via adapters.
// ============================================================

import React, { useMemo, useState } from 'react';
import { listReports, getReport, DEFAULT_REPORT_ID, type ReportsData } from './reportsModel';

const StatCell: React.FC<{ label: string; value: string; sub?: string; subColor?: string }> = ({ label, value, sub, subColor }) => (
  <div className="bg-[#F5F7FA] rounded-lg px-3 py-2.5">
    <div className="text-[8.5px] font-bold uppercase tracking-[0.08em] text-[#717182]">{label}</div>
    <div className="text-[15px] font-bold text-[#18365E] mt-0.5">{value}</div>
    {sub && <div className="text-[8.5px]" style={{ color: subColor ?? '#717182' }}>{sub}</div>}
  </div>
);

const ExecOnePager: React.FC<{ r: ReportsData }> = ({ r }) => (
  <div className="bg-white border border-[#e5e7eb] rounded shadow-[0_2px_10px_rgba(0,0,0,0.08)] px-8 py-8 max-w-[720px] w-full">
    <div className="flex justify-between items-start border-b-[3px] border-[#18365E] pb-3.5">
      <div>
        <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#FF6E4C]">ACME Health System · Enterprise Strategy Committee</div>
        <div className="text-[19px] font-bold text-[#18365E] mt-1.5 leading-tight">{r.eyebrowTitle}</div>
        <div className="text-[11px] text-[#717182] mt-1">Executive summary · {r.sourceLabel}</div>
      </div>
      <div className="w-8 h-8 rounded-lg bg-[#FF6E4C] flex-none" />
    </div>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 my-4">
      <StatCell label="Capital cap" value={`$${r.cap.toFixed(1)}M`} sub={r.capNote} subColor={r.capNoteReduction ? '#b91c1c' : '#717182'} />
      <StatCell label="Funded" value={`${r.funded} of ${r.total}`} sub={r.fundedNote} subColor="#b45309" />
      <StatCell label="Capital committed" value={`$${r.committed.toFixed(1)}M`} sub={`of $${r.cap.toFixed(1)}M cap`} />
      <StatCell label="Decisions" value={`${r.decisions.length} logged`} sub={`${r.approvedCount} approved · ${r.deferredCount} deferred`} />
    </div>

    <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#18365E] mb-1.5">Decisions for ratification</div>
    {r.decisions.length === 0 ? (
      <div className="border border-[#e5e7eb] rounded-lg px-3 py-3 mb-3.5 text-[10px] text-[#9ca3af]">No governance decisions logged under this run.</div>
    ) : (
      <div className="border border-[#e5e7eb] rounded-lg overflow-hidden mb-3.5">
        {r.decisions.map((d, i) => (
          <div key={i} className="flex gap-2.5 items-center px-3 py-2 border-b border-[#f1f3f6] last:border-b-0">
            <span className={`text-[9px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 shrink-0 ${d.badgeCls}`}>{d.decision}</span>
            <span className="flex-1 text-[10.5px] font-semibold text-[#18365E] min-w-0 truncate">{d.title}</span>
            <span className="text-[9px] text-[#717182] max-w-[280px] truncate hidden md:block">{d.by}</span>
          </div>
        ))}
      </div>
    )}

    <div className="flex flex-col md:flex-row gap-3.5">
      <div className="flex-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#18365E] mb-1.5">{r.changeTitle}</div>
        <div className="text-[10px] leading-relaxed text-[#374151]">{r.changeSummary}</div>
      </div>
      <div className="md:w-[200px] flex-none">
        <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#18365E] mb-1.5">Method</div>
        <div className="text-[10px] leading-relaxed text-[#374151]">Four-dimension scoring → composite + confidence, ranked cumulatively against the capital cap. Full audit trail in the DVA decision record.</div>
      </div>
    </div>

    <div className="border-t border-[#e5e7eb] mt-3.5 pt-2 flex justify-between text-[8.5px] text-[#9ca3af]">
      <span>Prepared by D. Whitfield, VP Enterprise Strategy · for the Enterprise Strategy Committee</span>
      <span>Generated from the DVA decision record · page 1 of 1</span>
    </div>
  </div>
);

const BoardDeck: React.FC<{ r: ReportsData }> = ({ r }) => (
  <>
    <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#717182]">Board Rationale · PPTX — 4 slides</div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Slide 1 — title */}
      <div className="bg-[#18365E] rounded-[10px] relative overflow-hidden aspect-[16/9]">
        <div className="absolute -right-14 -top-14 w-60 h-60 rounded-full bg-[#FF6E4C]/[0.12]" />
        <div className="absolute left-8 top-7 right-8">
          <div className="text-[8.5px] font-bold uppercase tracking-[0.16em] text-[#FF6E4C]">ACME Health System · Board of Directors · {r.boardDate}</div>
          <div className="text-[21px] font-bold text-white leading-[1.15] mt-2.5">
            {r.deckTitle.split('\n').map((line, i) => (<React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>))}
          </div>
          <div className="text-[9.5px] text-[#b7c4d8] mt-2">{r.subtitle}</div>
        </div>
        <div className="absolute left-8 right-8 bottom-6 flex gap-2.5">
          {r.cards.map(([k, v]) => (
            <div key={k} className="flex-1 bg-white/[0.07] border border-white/[0.14] rounded-md px-2.5 py-2">
              <div className="text-[7px] font-bold uppercase tracking-[0.1em] text-[#8fa5c4]">{k}</div>
              <div className="text-[9px] text-white font-semibold mt-0.5">{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Slide 2 — the decisions */}
      <div className="bg-white border border-[#e5e7eb] rounded-[10px] overflow-hidden aspect-[16/9] px-8 py-6">
        <div className="text-[8.5px] font-bold uppercase tracking-[0.14em] text-[#FF6E4C]">Slide 2 · The decisions and why</div>
        <div className="text-base font-bold text-[#18365E] mt-1.5">{r.decisions.length} decision{r.decisions.length === 1 ? '' : 's'}, each with rationale on the record</div>
        <div className="flex flex-col gap-2 mt-3.5 overflow-hidden">
          {r.decisions.length === 0 ? (
            <div className="text-[10px] text-[#9ca3af]">No governance decisions under this run.</div>
          ) : (
            r.decisions.slice(0, 4).map((d, i) => (
              <div key={i} className="flex gap-2.5 items-start border border-[#f1f3f6] rounded-md px-2.5 py-2">
                <span className={`text-[9px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 shrink-0 ${d.badgeCls}`}>{d.decision}</span>
                <div className="min-w-0">
                  <div className="text-[10px] font-bold text-[#18365E] truncate">{d.title}</div>
                  <div className="text-[8.5px] text-[#717182] leading-snug line-clamp-2">{d.rationale}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Slide 3 — before / after (or baseline) funding line */}
      <div className="bg-white border border-[#e5e7eb] rounded-[10px] overflow-hidden aspect-[16/9] px-8 py-6">
        <div className="text-[8.5px] font-bold uppercase tracking-[0.14em] text-[#FF6E4C]">Slide 3 · {r.hasComparison ? 'Before / after the funding line' : 'The funding line'}</div>
        <div className="text-base font-bold text-[#18365E] mt-1.5">{r.slide3Title}</div>
        <div className="flex gap-6 mt-4 items-end">
          {r.hasComparison && (
            <>
              <div className="flex-1">
                <div className="text-[9px] font-bold text-[#717182] mb-1.5">{r.beforeLabel}</div>
                <div className="flex h-6 rounded-md overflow-hidden bg-[#e5e7eb]"><div className="bg-[#18365E]" style={{ width: `${r.beforeBarPct}%` }} /></div>
                <div className="text-[8.5px] text-[#717182] mt-1.5"><b className="text-[#18365E]">{r.beforeFunded} funded</b> · {r.total} total</div>
              </div>
              <div className="text-[18px] text-[#b91c1c] font-bold pb-5">→</div>
            </>
          )}
          <div className="flex-1">
            <div className="text-[9px] font-bold text-[#717182] mb-1.5">{r.afterLabel}</div>
            <div className="flex h-6 rounded-md overflow-hidden bg-[#e5e7eb]">
              <div className="bg-[#18365E]" style={{ width: `${r.afterBarPct}%` }} />
              {r.hasComparison && <div className="bg-[#FF6E4C]" style={{ width: `${r.crossedBarPct}%` }} />}
            </div>
            <div className="text-[8.5px] text-[#717182] mt-1.5">
              <b className="text-[#18365E]">{r.afterFunded} funded</b>
              {r.hasComparison && <> · <b className="text-[#FF6E4C]">{r.crossed} crossed below</b></>} · {r.total} total
            </div>
          </div>
        </div>
        <div className="text-[9px] text-[#374151] leading-relaxed mt-4">{r.slide3Note}</div>
      </div>

      {/* Slide 4 — method appendix */}
      <div className="bg-white border border-[#e5e7eb] rounded-[10px] overflow-hidden aspect-[16/9] px-8 py-6">
        <div className="text-[8.5px] font-bold uppercase tracking-[0.14em] text-[#FF6E4C]">Slide 4 · Method appendix</div>
        <div className="text-base font-bold text-[#18365E] mt-1.5">One defensible framework, applied continuously</div>
        <div className="grid grid-cols-2 gap-2.5 mt-3.5">
          {[
            ['Scoring', 'Financial Impact · Operational Urgency · Strategic Alignment · Implementation Feasibility → weighted composite + confidence'],
            ['Weights this run', `${r.weightsLabel} — recorded with the run/scenario`],
            ['Governance', 'Strategy-committee review → CFO capital gate → decision logged with rationale, owner, and timestamp'],
            ['Audit', 'Immutable runs (scoring_run) + decision_log; any past state reconstructable "as of" that run'],
          ].map(([k, v]) => (
            <div key={k} className="bg-[#F5F7FA] rounded-md px-3 py-2.5">
              <div className="text-[8px] font-bold uppercase tracking-[0.1em] text-[#717182]">{k}</div>
              <div className="text-[9px] text-[#374151] leading-snug mt-0.5">{v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </>
);

export const ReportsSurface: React.FC = () => {
  const entries = useMemo(() => listReports(), []);
  const [selectedId, setSelectedId] = useState<string>(DEFAULT_REPORT_ID);
  const selected = entries.find((e) => e.id === selectedId) ?? entries[0];
  const r = useMemo(() => getReport(selectedId), [selectedId]);
  const noop = () => { /* export wiring deferred — see summary */ };

  return (
    <div className="min-h-[calc(100vh-180px)] bg-[#F5F7FA]">
      <div className="px-6 py-6 flex flex-col gap-5">
        {/* Header */}
        <div>
          <div className="text-base font-bold text-[#18365E]">Report library</div>
          <div className="text-[11px] text-[#717182] mt-0.5">
            Governance-ready outputs generated over time from saved scenarios + the decision record · for the Enterprise Strategy Committee / Board
          </div>
        </div>

        {/* Library cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {entries.map((e) => {
            const active = e.id === selectedId;
            return (
              <button
                key={e.id}
                onClick={() => setSelectedId(e.id)}
                className={`text-left bg-white border rounded-xl px-4 py-3.5 transition-shadow ${active ? 'border-[#FF6E4C] outline outline-2 outline-[#FF6E4C]/30' : 'border-[#e5e7eb] hover:shadow-sm'}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[9px] font-bold uppercase tracking-[0.1em] text-[#717182]">{e.date}</div>
                  {e.id === 'rep-q2' && <span className="text-[8.5px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#dcfce7] text-[#15803d]">Live</span>}
                </div>
                <div className="text-[13.5px] font-bold text-[#18365E] mt-1 leading-snug">{e.title}</div>
                <div className="text-[10px] text-[#717182] mt-1">{e.source}</div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {e.types.map((t) => (
                    <span key={t} className="text-[8.5px] font-semibold text-[#374151] bg-[#F5F7FA] border border-[#e5e7eb] rounded-full px-2 py-0.5">{t}</span>
                  ))}
                </div>
              </button>
            );
          })}
        </div>

        {/* Selected report detail */}
        <div className="flex flex-wrap justify-between items-center gap-3 border-t border-[#e5e7eb] pt-4">
          <div className="text-[11px] text-[#717182]">
            Showing <b className="text-[#18365E]">{selected?.title}</b> · {selected?.source}
          </div>
          <div className="flex gap-2">
            {(selected?.types ?? []).map((t) => (
              <button key={t} onClick={noop} title="Export wiring pending" className="border-[1.5px] border-[#18365E] text-[#18365E] rounded-lg px-3.5 py-2 text-[11.5px] font-semibold bg-white hover:bg-[#18365E]/[0.04]">
                ↓ {t}
              </button>
            ))}
          </div>
        </div>

        {!r.found ? (
          <div className="px-6 py-16 text-center text-[12px] text-[#9ca3af]">No data for this report.</div>
        ) : (
          <>
            <ExecOnePager r={r} />
            <BoardDeck r={r} />
          </>
        )}
      </div>
    </div>
  );
};

export default ReportsSurface;
